import { motion } from 'framer-motion';
import { Check, ChevronDown, Clipboard, LoaderCircle, SearchX, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { copyText } from '../lib/format';
import { CountUp, Tilt } from './motion';

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <motion.header
      className="page-heading"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
    >
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1 className="gradient-text">{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="heading-actions">{actions}</div> : null}
    </motion.header>
  );
}

export function StatCard({
  label,
  value,
  detail,
  icon: Icon,
  tone = 'violet',
  delay = 0,
}: {
  label: string;
  value: string;
  detail?: ReactNode;
  icon: LucideIcon;
  tone?: 'violet' | 'cyan' | 'pink' | 'amber' | 'green';
  delay?: number;
}) {
  return (
    <motion.article
      className="stat-shell"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
    >
      <Tilt className={`panel stat-card tone-${tone}`}>
        <div className="stat-icon"><Icon size={20} aria-hidden /></div>
        <span>{label}</span>
        <strong><CountUp value={value} /></strong>
        {detail ? <small>{detail}</small> : null}
      </Tilt>
    </motion.article>
  );
}

export function StatusPill({ children, tone = 'neutral' }: { children: ReactNode; tone?: string }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function ProgressBar({ value, tone = 'violet', label }: { value: number; tone?: string; label?: string }) {
  const safe = Math.max(0, Math.min(100, value));
  return (
    <div className="progress-wrap" aria-label={label ?? `${safe}% complete`}>
      <div className="progress-track">
        <motion.div
          className={`progress-fill progress-${tone}`}
          initial={{ width: 0 }}
          animate={{ width: `${safe}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
      {label ? <span>{label}</span> : null}
    </div>
  );
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="icon-button copy-button"
      type="button"
      aria-label={`${label} ${value}`}
      onClick={() => {
        void copyText(value).then((ok) => {
          setCopied(ok);
          if (ok) window.setTimeout(() => setCopied(false), 1_500);
        });
      }}
    >
      {copied ? <Check size={15} /> : <Clipboard size={15} />}
    </button>
  );
}

export function EmptyState({
  title,
  description,
  action,
  loading = false,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{loading ? <LoaderCircle className="spin" /> : <SearchX />}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Shimmer({ className = '' }: { className?: string }) {
  return <span className={`shimmer ${className}`} aria-hidden />;
}

/**
 * Centres a horizontally-scrollable container on its content. The generation tree is wider than a
 * phone viewport, and a scroller opens at scrollLeft 0 — which put the blank left margin on screen
 * and the root node off it, reading as a rendering failure rather than a scrollable diagram.
 */
export function useCenteredScroll<T extends HTMLElement>(dependency: unknown): (node: T | null) => void {
  const nodeRef = useRef<T | null>(null);
  const userScrolledRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const centre = useCallback((element: T) => {
    if (userScrolledRef.current) return;
    const overflow = element.scrollWidth - element.clientWidth;
    if (overflow <= 0) return;
    const target = Math.round(overflow / 2);
    if (Math.abs(element.scrollLeft - target) > 1) element.scrollLeft = target;
  }, []);

  // A callback ref, not a RefObject: the node attaches on a later render than the one that runs
  // the first effect, so an effect-based version silently saw a null ref and never centred.
  const attach = useCallback(
    (node: T | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      nodeRef.current = node;
      if (!node) return;
      userScrolledRef.current = false;
      const run = () => centre(node);
      const markUserScroll = () => {
        userScrolledRef.current = true;
      };
      run();
      // Layout, entrance animation and font swap each change scrollWidth after attach.
      const frame = window.requestAnimationFrame(run);
      const timers = [80, 250, 600, 1_200].map((delay) => window.setTimeout(run, delay));
      const observer = new ResizeObserver(run);
      observer.observe(node);
      const grid = node.firstElementChild;
      if (grid) observer.observe(grid);
      node.addEventListener('pointerdown', markUserScroll, { passive: true });
      node.addEventListener('wheel', markUserScroll, { passive: true });
      node.addEventListener('touchstart', markUserScroll, { passive: true });
      cleanupRef.current = () => {
        window.cancelAnimationFrame(frame);
        timers.forEach((timer) => window.clearTimeout(timer));
        observer.disconnect();
        node.removeEventListener('pointerdown', markUserScroll);
        node.removeEventListener('wheel', markUserScroll);
        node.removeEventListener('touchstart', markUserScroll);
      };
    },
    [centre],
  );

  // Re-centre when the rendered content changes without the node being replaced.
  useEffect(() => {
    userScrolledRef.current = false;
    const node = nodeRef.current;
    if (!node) return;
    const run = () => centre(node);
    const frame = window.requestAnimationFrame(run);
    const timers = [80, 250, 600].map((delay) => window.setTimeout(run, delay));
    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [dependency, centre]);

  return attach;
}

/**
 * Connector bar between two generations of a tree. One drop line is emitted per actual child and
 * positioned from the child count, because the previous fixed 3/5 stubs at hard-coded percentages
 * pointed into empty space whenever the real number of children differed.
 */
export function TreeConnector({ count, variant }: { count: number; variant: 'one' | 'two' }) {
  if (count <= 0) return null;
  const centreOf = (index: number) => ((index + 0.5) / count) * 100;
  const start = centreOf(0);
  const end = centreOf(count - 1);
  return (
    <div
      className={`tree-lines lines-${variant}`}
      style={{ '--line-start': `${start}%`, '--line-end': `${end}%` } as CSSProperties}
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <i key={index} style={{ left: `${centreOf(index)}%` }} />
      ))}
    </div>
  );
}

/**
 * A fully custom-styled dropdown, used instead of a native <select> wherever the popup list needs
 * to reliably match the app's dark theme — native select popups render with OS/browser default
 * (often white) styling that CSS cannot control, regardless of `color-scheme`.
 */
export function Dropdown<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  small = false,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange(value: T): void;
  ariaLabel: string;
  small?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocumentPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDocumentPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocumentPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const current = options.find((option) => option.value === value);

  return (
    <div className={`app-dropdown${small ? ' small' : ''}`} ref={rootRef}>
      <button type="button" className="app-dropdown-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((previous) => !previous)}>
        <span>{current?.label ?? String(value)}</span>
        <ChevronDown size={14} />
      </button>
      {open ? (
        <ul className="app-dropdown-list" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <li key={String(option.value)}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={option.value === value ? 'active' : ''}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange(value: T): void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? 'active' : ''}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
